/**
 * Тема golf, передаваемая в прилоежние при запуске {@link GolfTheme} должна быть преобразована
 * в тему, готовую для использования {@link AppTheme}
 */

import map from '@tinkoff/utils/object/map'
import { AppTheme, CssStyle, Font } from 'application/theme/appTheme'
import {
  AnyBreakpoints,
  BaseBreakpoints,
  BreakpointAlias,
  Breakpoints,
  GolfBreakpoints
} from 'application/theme/appTheme'
import { FontCategory, FontStyle, GolfTheme } from 'application/theme/golf/types'
import { getValueForBreakpoint } from 'lib/get-value-for-breakpoint'

import { CorvetteTheme } from '../corvette/types'
import { multiplyRems } from '../utils'

export const BREAKPOINTS_ORDER: BreakpointAlias[] = ['phone', 'phablet', 'tablet', 'desktop', 'projectors']

/**
 * Нормализует описание стиля текста из формата external/FontStyle в обычный CssStyleDeclaration
 * @param textStyle
 * @param theme
 */
export const normalizeExternalTextStyle = (textStyle: FontStyle, theme: GolfTheme): CssStyle => {
  const style: CssStyle = {
    fontWeight: textStyle.fontWeight,
    fontSize: textStyle.fontSize,
    fontFamily:
      resolveFont(textStyle.family, theme).family +
      ',-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto" ,"Helvetica Neue", Arial, sans-serif,  "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    lineHeight: textStyle.lineHeight
  }

  if (textStyle.offset?.marginBottom) style.marginBottom = textStyle.offset.marginBottom
  if (textStyle.offset?.marginTop) style.marginTop = textStyle.offset.marginTop

  return style
}

/**
 * Очищает стиль от всего, что не связано с текстовым представлением
 * @param styles
 */
export const extractPureTextStyles = (styles: CssStyle): CssStyle => {
  const { fontWeight, fontSize, fontFamily } = styles
  return { fontWeight, fontSize, fontFamily }
}

/**
 * Извлекает из темы шрифт заданой категории и нормализует его
 */
export const resolveFont = (fontCategory: FontCategory, theme: GolfTheme): Font => {
  const font = theme.typography.fonts.find((font) => font.category === fontCategory)
  if (!font) throw Error(`Theming error: font of category ${fontCategory} was requested but not declared in theme`)
  return { ...font, options: font.options.map((option) => ({ fontWeight: option.weight })) }
}

/**
 * Возвращает название текущего брейкпоинта, приняв настройками ExternalTheme.grid.breakpoints и значение ширины экрана
 * @param gridBreakpoints
 * @param screenWidth
 */
export const getCurrentBreakpointAlias = (gridBreakpoints: BaseBreakpoints, screenWidth: number): BreakpointAlias => {
  const breakpoint = BREAKPOINTS_ORDER.filter((alias) => {
    const minWidthNumber = parseInt(gridBreakpoints[alias].minWidth, 10)
    return screenWidth >= minWidthNumber
  }).pop()

  return breakpoint || BREAKPOINTS_ORDER[0]
}

export const createMediaQueries = (gridSettings: BaseBreakpoints) => {
  const queries = {} as Breakpoints<string>

  BREAKPOINTS_ORDER.forEach((breakpoint) => {
    queries[breakpoint] = `(min-width:${gridSettings[breakpoint].minWidth})`
  })

  return queries
}

const internalizeBreakpoints = (external: GolfBreakpoints<any>): AnyBreakpoints<any> => {
  if ('none' in external) {
    return {
      phone: external['none']
    }
  }

  return external
}

export const normalizePalette = (palette: GolfTheme['palette']): CorvetteTheme['colors'] => {
  return {
    black: palette.black,
    white: palette.white,

    grey: {
      '50': palette.grey10,
      '100': palette.grey9,
      '200': palette.grey8,
      '300': palette.grey7,
      '400': palette.grey6,
      '500': palette.grey5,
      '600': palette.grey4,
      '700': palette.grey3,
      '800': palette.grey2,
      '900': palette.grey1
    },

    info: palette.info,
    success: palette.success,
    warning: palette.warning,
    alert: palette.error,

    brand: {
      primary: palette.brandPrimary1,
      primaryLight: palette.brandPrimary1,
      primaryDark: palette.brandPrimary2,
      secondary: palette.brandSecondary1,
      secondaryLight: palette.brandSecondary1,
      secondaryDark: palette.brandSecondary2
    }
  }
}

/**
 * Создаёт объект темы для конкретного брейкпоинта, взяв за основу объект темы проекта
 * Автоматически извлекает только те значения, которые применимы на текущей ширине экрана
 * @param theme
 * @param screenWidth
 */
export const golfToAppTheme = (theme: GolfTheme, screenWidth: number): AppTheme => {
  const currentBreakpointName = getCurrentBreakpointAlias(theme.grid.breakpoints, screenWidth)

  const { h1, h2, h3, h4, h5, h6 } = theme.typography.title
  const { p1, p2, p3, p4, btn } = theme.typography.body

  const normalizedStyles = map(
    (object) => {
      const styles = getValueForBreakpoint(internalizeBreakpoints(object), currentBreakpointName)
      return normalizeExternalTextStyle(styles, theme)
    },
    { h1, h2, h3, h4, h5, h6, p1, p2, p3, p4, btn }
  )

  return {
    mediaQueries: createMediaQueries(theme.grid.breakpoints),
    activeBreakpoint: currentBreakpointName,
    grid: getValueForBreakpoint(theme.grid.breakpoints, currentBreakpointName),
    palette: normalizePalette(theme.palette),
    fonts: {
      heading: resolveFont('primary', theme),
      body: resolveFont('secondary', theme),
      accent: resolveFont('secondary', theme)
    },
    textStyles: {
      h1: extractPureTextStyles(normalizedStyles.h1),
      h2: extractPureTextStyles(normalizedStyles.h2),
      h3: extractPureTextStyles(normalizedStyles.h3),
      h4: extractPureTextStyles(normalizedStyles.h4),
      h5: extractPureTextStyles(normalizedStyles.h5),
      h6: extractPureTextStyles(normalizedStyles.h6),
      p1: extractPureTextStyles(normalizedStyles.p1),
      p2: extractPureTextStyles(normalizedStyles.p2),
      p3: extractPureTextStyles(normalizedStyles.p3),
      p4: extractPureTextStyles(normalizedStyles.p4),
      btn: extractPureTextStyles(normalizedStyles.btn)
    },
    typography: {
      title: {
        h1: normalizedStyles.h1,
        h2: normalizedStyles.h2,
        h3: normalizedStyles.h3,
        h4: normalizedStyles.h4,
        h5: normalizedStyles.h5,
        h6: normalizedStyles.h6
      },
      paragraph: {
        p1: normalizedStyles.p1,
        p2: normalizedStyles.p2,
        p3: normalizedStyles.p3,
        p4: normalizedStyles.p4
      }
    },
    transition: null,
    shadows: null,
    spacing: {
      '0': multiplyRems('0', 1.6),
      '1': multiplyRems('0.25rem', 1.6),
      '2': multiplyRems('0.5rem', 1.6),
      '3': multiplyRems('0.75rem', 1.6),
      '4': multiplyRems('1rem', 1.6),
      '5': multiplyRems('1.25rem', 1.6),
      '6': multiplyRems('1.5rem', 1.6),
      '7': multiplyRems('1.75rem', 1.6),
      '8': multiplyRems('2rem', 1.6),
      '9': multiplyRems('2.25rem', 1.6),
      '10': multiplyRems('2.5rem', 1.6),
      '11': multiplyRems('2.75rem', 1.6),
      '12': multiplyRems('3rem', 1.6),
      '14': multiplyRems('3.5rem', 1.6),
      '16': multiplyRems('4rem', 1.6),
      '18': multiplyRems('4.5rem', 1.6),
      '20': multiplyRems('5rem', 1.6),
      '24': multiplyRems('6rem', 1.6),
      '28': multiplyRems('7rem', 1.6),
      '32': multiplyRems('8rem', 1.6),
      '36': multiplyRems('9rem', 1.6),
      '40': multiplyRems('10rem', 1.6),
      '44': multiplyRems('11rem', 1.6),
      '48': multiplyRems('12rem', 1.6),
      '52': multiplyRems('13rem', 1.6),
      '56': multiplyRems('14rem', 1.6),
      '60': multiplyRems('15rem', 1.6),
      '64': multiplyRems('16rem', 1.6),
      '72': multiplyRems('18rem', 1.6),
      '80': multiplyRems('20rem', 1.6),
      '96': multiplyRems('24rem', 1.6)
    }
  }
}
